/**
 * Commission Matching Page
 * Route: /commission/matching
 *
 * Displays matching statistics and a queue of unmatched commission transactions.
 * Supports auto-match (all pending) and manual match (per row dropdown).
 *
 * States: Statistics cards + Queue table | Auto-match running | Complete
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { format } from "date-fns";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CommissionTransaction {
  id: string;
  transactionDate: string | null;
  lenderName: string | null;
  lenderAccountNumber: string | null;
  clientNameFromFile: string | null;
  commissionAmount: string | null;
  totalAmount: string | null;
  processingStatus: string | null;
  facilityId: string | null;
  dealId: string | null;
  matchedPartnerId: string | null;
}

interface MatchSuggestion {
  facilityId: string;
  facilityName: string;
  dealName: string;
  confidence: number;
  method: string;
}

interface AutoMatchResponse {
  matchedCount: number;
  unmatchedCount: number;
  matches: Array<{
    transactionId: string;
    facilityId: string;
    confidence: number;
  }>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatCurrency = (value: string | null | undefined): string => {
  if (!value) return "-";
  const num = parseFloat(value);
  if (isNaN(num)) return "-";
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
};

const formatDate = (value: string | null | undefined): string => {
  if (!value) return "-";
  try {
    return format(new Date(value), "dd/MM/yyyy");
  } catch {
    return value;
  }
};

// ---------------------------------------------------------------------------
// Statistics card
// ---------------------------------------------------------------------------

interface StatCardProps {
  testId: string;
  title: string;
  value: string | number;
  subtitle?: string;
  className?: string;
}

function StatCard({ testId, title, value, subtitle, className }: StatCardProps) {
  return (
    <Card data-testid={testId} className={className}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {subtitle && (
          <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Queue row
// ---------------------------------------------------------------------------

interface QueueRowProps {
  tx: CommissionTransaction;
  onManualMatch: (transactionId: string, facilityId: string) => void;
  onIgnore: (transactionId: string) => void;
  matchPending: boolean;
  ignorePending: boolean;
  selectedFacilityId: string;
  onFacilitySelect: (facilityId: string) => void;
}

function QueueRow({
  tx,
  onManualMatch,
  onIgnore,
  matchPending,
  ignorePending,
  selectedFacilityId,
  onFacilitySelect,
}: QueueRowProps) {
  return (
    <TableRow data-testid={`row-queue-${tx.id}`}>
      <TableCell className="whitespace-nowrap text-sm">
        {formatDate(tx.transactionDate)}
      </TableCell>
      <TableCell className="text-sm">{tx.lenderName ?? "-"}</TableCell>
      <TableCell className="font-mono text-sm">{tx.lenderAccountNumber ?? "-"}</TableCell>
      <TableCell className="text-sm">{tx.clientNameFromFile ?? "-"}</TableCell>
      <TableCell className="text-right font-medium text-sm">
        {formatCurrency(tx.totalAmount)}
      </TableCell>
      <TableCell>
        <SuggestionsCell
          transactionId={tx.id}
          selectedFacilityId={selectedFacilityId}
          onSelect={onFacilitySelect}
        />
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            disabled={!selectedFacilityId || selectedFacilityId === "none" || matchPending}
            onClick={() => onManualMatch(tx.id, selectedFacilityId)}
            data-testid={`button-match-${tx.id}`}
          >
            {matchPending ? <Loader2 className="h-3 w-3 animate-spin" /> : "Match"}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-xs text-muted-foreground"
            disabled={ignorePending}
            onClick={() => onIgnore(tx.id)}
            data-testid={`button-ignore-${tx.id}`}
          >
            Ignore
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

// ---------------------------------------------------------------------------
// Suggestions cell (fetches suggestions per row)
// ---------------------------------------------------------------------------

interface SuggestionsCellProps {
  transactionId: string;
  selectedFacilityId: string;
  onSelect: (facilityId: string) => void;
}

function SuggestionsCell({ transactionId, selectedFacilityId, onSelect }: SuggestionsCellProps) {
  const { data: suggestions = [], isLoading } = useQuery<MatchSuggestion[]>({
    queryKey: ["/api/commissions/match/suggestions", transactionId],
    queryFn: async () => {
      const response = await apiRequest(
        "GET",
        `/api/commissions/match/suggestions/${transactionId}`
      );
      if (!response.ok) return [];
      return response.json();
    },
  });

  return (
    <div className="flex items-center gap-2 min-w-[200px]">
      {isLoading ? (
        <span className="text-xs text-muted-foreground">Loading...</span>
      ) : (
        <Select value={selectedFacilityId || "none"} onValueChange={onSelect}>
          <SelectTrigger
            className="h-8 text-xs flex-1"
            data-testid={`select-facility-${transactionId}`}
          >
            <SelectValue placeholder="Select facility..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">— Select facility —</SelectItem>
            {suggestions.map((s) => (
              <SelectItem key={s.facilityId} value={s.facilityId}>
                {s.facilityName}
                {s.confidence > 0 && (
                  <span className="ml-1 text-xs text-muted-foreground">
                    ({Math.round(s.confidence * 100)}%)
                  </span>
                )}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function CommissionMatchingPageContent() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Per-row selected facility state: transactionId → facilityId
  const [selectedFacilities, setSelectedFacilities] = useState<Record<string, string>>({});
  // Locally ignored transaction IDs (optimistic hide before queue refresh)
  const [ignoredIds, setIgnoredIds] = useState<Set<string>>(new Set());

  // -------------------------------------------------------------------------
  // Data fetching
  // -------------------------------------------------------------------------

  const { data: allTransactions = [], isLoading: txLoading } = useQuery<CommissionTransaction[]>({
    queryKey: ["/api/commissions/transactions", "pending"],
    queryFn: async () => {
      const response = await apiRequest(
        "GET",
        "/api/commissions/transactions?processingStatus=pending&limit=500"
      );
      if (!response.ok) return [];
      return response.json();
    },
  });

  const { data: allTxCounts = [], isLoading: countLoading } = useQuery<CommissionTransaction[]>({
    queryKey: ["/api/commissions/transactions", "all-counts"],
    queryFn: async () => {
      const response = await apiRequest(
        "GET",
        "/api/commissions/transactions?limit=1000"
      );
      if (!response.ok) return [];
      return response.json();
    },
  });

  // Filter out locally ignored rows
  const pendingTransactions = allTransactions.filter((tx) => !ignoredIds.has(tx.id));

  // Statistics
  const unmatchedCount = pendingTransactions.length;
  const totalCount = Array.isArray(allTxCounts) ? allTxCounts.length : 0;
  const matchedCount = Array.isArray(allTxCounts)
    ? allTxCounts.filter((tx) => tx.processingStatus === "matched").length
    : 0;
  const matchRate =
    totalCount > 0 ? Math.round((matchedCount / totalCount) * 100) : 0;
  const potentialMatches = pendingTransactions.length; // simplification: all pending have potential

  // -------------------------------------------------------------------------
  // Mutations
  // -------------------------------------------------------------------------

  const autoMatchMutation = useMutation<AutoMatchResponse, Error>({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/commissions/match/auto");
      return response.json() as Promise<AutoMatchResponse>;
    },
    onSuccess: (data) => {
      toast({
        title: "Auto-match complete",
        description: `Matched ${data.matchedCount} transaction${data.matchedCount !== 1 ? "s" : ""}`,
      });
      void queryClient.invalidateQueries({ queryKey: ["/api/commissions/transactions"] });
    },
    onError: (err) => {
      toast({
        title: "Auto-match failed",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const manualMatchMutation = useMutation<
    CommissionTransaction,
    Error,
    { transactionId: string; facilityId: string }
  >({
    mutationFn: async ({ transactionId, facilityId }) => {
      const response = await apiRequest("POST", "/api/commissions/match/manual", {
        transactionId,
        facilityId,
      });
      return response.json() as Promise<CommissionTransaction>;
    },
    onSuccess: (_, variables) => {
      toast({ title: "Transaction matched", description: "Linked to selected facility" });
      // Optimistically remove from queue
      setIgnoredIds((prev) => new Set(Array.from(prev).concat(variables.transactionId)));
      void queryClient.invalidateQueries({ queryKey: ["/api/commissions/transactions"] });
    },
    onError: (err) => {
      toast({
        title: "Match failed",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const handleIgnore = (transactionId: string) => {
    // Optimistically hide the row (no API call — just removes from local view)
    setIgnoredIds((prev) => new Set(Array.from(prev).concat(transactionId)));
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  const isLoading = txLoading || countLoading;

  return (
    <div className="mx-auto max-w-7xl space-y-4">
      {/* Statistics row */}
      <div className="grid grid-cols-3 gap-4">
        <StatCard
          testId="stat-card-unmatched"
          title="Unmatched"
          value={isLoading ? "..." : unmatchedCount}
          subtitle="Transactions awaiting matching"
        />
        <StatCard
          testId="stat-card-potential"
          title="Potential Matches"
          value={isLoading ? "..." : potentialMatches}
          subtitle="Transactions with suggestions available"
        />
        <StatCard
          testId="stat-card-match-rate"
          title="Match Rate"
          value={isLoading ? "..." : `${matchRate}%`}
          subtitle={`${matchedCount} of ${totalCount} transactions matched`}
        />
      </div>

      {/* Auto-match action bar */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {unmatchedCount > 0
            ? `${unmatchedCount} transaction${unmatchedCount !== 1 ? "s" : ""} pending matching`
            : "All transactions matched"}
        </p>
        <Button
          onClick={() => autoMatchMutation.mutate()}
          disabled={autoMatchMutation.isPending || unmatchedCount === 0}
          data-testid="button-auto-match"
        >
          {autoMatchMutation.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Matching...
            </>
          ) : (
            "Auto-Match All"
          )}
        </Button>
      </div>

      {/* Auto-match progress */}
      {autoMatchMutation.isPending && (
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
            <p className="text-sm">Running auto-match on all pending transactions...</p>
          </div>
        </Card>
      )}

      {/* Queue table */}
      {isLoading ? (
        <Card className="p-8 text-center text-muted-foreground">
          <Loader2 className="mx-auto h-6 w-6 animate-spin mb-2" />
          <p>Loading unmatched transactions...</p>
        </Card>
      ) : pendingTransactions.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground space-y-2">
          <CheckCircle2 className="mx-auto h-8 w-8 text-green-500" />
          <p className="font-medium">All transactions matched</p>
          <p className="text-sm">No unmatched transactions in the queue.</p>
        </Card>
      ) : (
        <Card>
          <Table data-testid="matching-queue-table">
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Lender</TableHead>
                <TableHead>Account #</TableHead>
                <TableHead>Client</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Suggested Match</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pendingTransactions.map((tx) => (
                <QueueRow
                  key={tx.id}
                  tx={tx}
                  onManualMatch={(transactionId, facilityId) =>
                    manualMatchMutation.mutate({ transactionId, facilityId })
                  }
                  onIgnore={handleIgnore}
                  matchPending={manualMatchMutation.isPending}
                  ignorePending={false}
                  selectedFacilityId={selectedFacilities[tx.id] ?? "none"}
                  onFacilitySelect={(facilityId) =>
                    setSelectedFacilities((prev) => ({
                      ...prev,
                      [tx.id]: facilityId,
                    }))
                  }
                />
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
